import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notification } from 'antd';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertTriangle,
  Ban,
  Check,
  CheckCheck,
  ChevronDown,
  FileQuestion,
  Flag,
  Pencil,
  Phone,
  Search,
  X,
} from 'lucide-react';
import useMaterias from '../../../core/hooks/UseMaterias';
import AccessDenied from '../AccessDenied/AccessDenied';
import ForumService from '../../../sdk/services/ForumService';
import { readApiError, type ApiError } from '../../../sdk/errors/apiError';
import type {
  ForumAuthor,
  ModerationComment,
  ModerationStats,
  RejectReason,
} from '../../../sdk/@types/Forum';

/**
 * Fila de moderação do fórum (aba "Fórum" de `/admin/moderacao`).
 *
 * Decisões que não são óbvias no código:
 *
 * - **As três sub-filas são buscadas no `mount`**, uma chamada cada. Isso existe
 *   para que aprovar/rejeitar possa mover o card de Pendentes para a sub-fila de
 *   destino **em memória**: se Aprovados fosse buscado só ao abrir a aba, a
 *   resposta do servidor sobrescreveria o card que acabou de ser movido.
 * - **Busca e filtro de matéria são client-side.** A fila inteira já está em
 *   memória; refazer a chamada a cada tecla não acrescentaria nada. O serviço
 *   aceita `q`/`subjectId` para quando houver volume — ver `ModerationListParams`.
 * - **Matéria vem de `useMaterias()` (`GET /subjects`)**, nunca da chamada
 *   escopada por assinante: admin não tem registro de assinante, então a
 *   subscriber-scoped devolve lista vazia justamente para quem usa esta tela
 *   (mesma armadilha do R12).
 * - **Atalhos são `onKeyDown` no card focado** (`tabIndex={0}`, padrão do
 *   `PdfAnnotator`), sem listener global: com listener global a tecla `a` digitada
 *   na caixa de mensagem aprovaria o card.
 * - **404/409 de uma ação = "já resolvido"**: outro admin chegou antes. O card sai
 *   da fila com um aviso informativo, não um erro — não há realtime aqui (o hub
 *   `/api/ws` é escopado a simulado).
 * - **Cor:** estados de moderação na família `warn`; `danger` só em "Confirmar
 *   rejeição", que é a ação destrutiva. O frame do Pencil pinta a flag "Contém
 *   telefone" de vermelho; aqui ela é `warn` de propósito — a flag é aviso, não
 *   erro, e nunca bloqueia a aprovação.
 */

const surface = (token: string) => `rgb(var(${token}))`;

/**
 * Toast de moderação (comentário já decidido por outro admin, falha ao
 * aprovar/rejeitar…).
 *
 * Mesma armadilha de `notifyReportSent` (`ReportQuestionModal.tsx`) e
 * `notifyDenial`: a API estática do antd (`notification.info`/`.error`)
 * renderiza fora do único `ConfigProvider` do app (`Default.layout.tsx`), que
 * é quem liga o `darkAlgorithm` — o toast nasceria sempre no tema claro do
 * antd, não no tema da página. `notification.open` com `message` em JSX
 * (tokens Tailwind) e `style` construído a partir dos tokens resolve isso.
 * `warn`, não `danger`: nenhum destes avisos é a ação destrutiva da tela —
 * essa é "Confirmar rejeição", a única com `danger`.
 */
function notifyModeration(message: string): void {
  notification.open({
    message: (
      <div className="flex items-start gap-2 font-sans">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warn-ink" aria-hidden />
        <p className="m-0 text-sm text-ink">{message}</p>
      </div>
    ),
    description: null,
    placement: 'topRight',
    duration: 6,
    closeIcon: <X size={16} style={{ color: surface('--c-text-muted') }} />,
    style: {
      background: surface('--c-surface'),
      color: surface('--c-text'),
      border: `1px solid ${surface('--c-border')}`,
      borderRadius: 14,
      boxShadow: 'var(--shadow-panel)',
      padding: 16,
    },
  });
}

type QueueStatus = 'pending' | 'approved' | 'rejected';

export interface ModerationQueueProps {
  /**
   * KPIs do dia. A view já busca `/moderation/stats` para os badges das abas e
   * repassa; sem a prop, a fila busca por conta própria (uso isolado/Storybook).
   */
  stats?: ModerationStats | null;
  /** Quantos comentários saíram de Pendentes — o badge da aba decrementa. */
  onPendingResolved?: (count: number) => void;
}

const STATUS_TABS: { key: QueueStatus; label: string }[] = [
  { key: 'pending', label: 'Pendentes' },
  { key: 'approved', label: 'Aprovados' },
  { key: 'rejected', label: 'Rejeitados' },
];

export const REJECT_REASONS: { key: RejectReason; label: string }[] = [
  { key: 'spam', label: 'Spam ou propaganda' },
  { key: 'offensive', label: 'Ofensivo' },
  { key: 'offtopic', label: 'Fora do tema' },
  { key: 'incorrect', label: 'Informação incorreta' },
  { key: 'other', label: 'Outro' },
];

const GUIDELINES: { allowed: boolean; text: string }[] = [
  { allowed: true, text: 'Dicas, macetes e dúvidas sobre a questão' },
  { allowed: true, text: 'Referências a leis, súmulas e doutrina' },
  { allowed: false, text: 'Contatos, links de venda ou material pirata' },
  { allowed: false, text: 'Ofensas, ironia com colegas ou spam' },
  { allowed: false, text: 'Respostas de outras questões fora do contexto' },
];

/**
 * Telefone BR, inclusive mascarado ("(62) 9 8xxx-xxxx"), porque o aluno que
 * anuncia material pirata costuma ofuscar o número. A flag oficial vem do
 * backend (`flags: ['phone']`); este regex é o fallback de exibição enquanto a
 * rota não existe — e os dois são só aviso visual.
 */
const BR_PHONE = /(?:\+?55[\s.-]?)?\(?\d{2}\)?[\s.-]?9?[\s.-]?[\dxX]{4}[\s.-]?[\dxX]{4}/;

export function hasPhoneFlag(item: ModerationComment): boolean {
  return (item.flags ?? []).includes('phone') || BR_PHONE.test(item.body ?? '');
}

const fold = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function roleLabel(author: ForumAuthor): string {
  // `label` é a turma/cargo do cadastro (CHOA, CADETE…); `role` é o papel no
  // fórum — `administrador`/`master` chegam como `professor`/`moderador`.
  if (author.label) return author.label;
  if (author.role === 'professor') return 'Professor';
  if (author.role === 'moderador') return 'Moderador';
  return 'Aluno';
}

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
}

export function questionRefLabel(item: ModerationComment): string {
  const { number, subject, agency, year } = item.question ?? {};
  const bancaAno = [agency, year].filter(Boolean).join(' ');
  return [number ? `Questão #${number}` : 'Questão', subject, bancaAno]
    .filter(Boolean)
    .join(' · ');
}

/** 404 = já não existe na fila; 409 = outro admin já decidiu. */
function isAlreadyResolved(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 404 || status === 409;
}

const CHIP =
  'inline-flex items-center gap-1 rounded-chip border px-2 py-0.5 text-xs font-medium';

const BTN =
  'inline-flex h-8 items-center gap-1.5 rounded-input border px-3 text-xs font-semibold ' +
  'transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60';

const BTN_NEUTRAL = `${BTN} border-edge bg-surface text-ink-secondary hover:border-edge-strong hover:bg-surface-hover hover:text-ink`;

export default function ModerationQueue({ stats, onPendingResolved }: ModerationQueueProps) {
  const { materias, fetchMaterias } = useMaterias();

  const [status, setStatus] = useState<QueueStatus>('pending');
  const [lists, setLists] = useState<Record<QueueStatus, ModerationComment[]>>({
    pending: [],
    approved: [],
    rejected: [],
  });
  const [loading, setLoading] = useState(true);
  const [denial, setDenial] = useState<ApiError | null>(null);

  const [search, setSearch] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');

  const [selected, setSelected] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<RejectReason | null>(null);
  const [rejectMessage, setRejectMessage] = useState('');
  const [busy, setBusy] = useState<string[]>([]);
  const [batching, setBatching] = useState(false);

  const [focusedIndex, setFocusedIndex] = useState(0);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [ownStats, setOwnStats] = useState<ModerationStats | null>(null);
  const kpis = stats ?? ownStats;

  useEffect(() => {
    fetchMaterias();
  }, [fetchMaterias]);

  useEffect(() => {
    if (stats !== undefined) return;
    ForumService.getModerationStats()
      .then(setOwnStats)
      .catch(() => setOwnStats(null));
  }, [stats]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all(
      STATUS_TABS.map((tab) => ForumService.listModerationComments({ status: tab.key }))
    )
      .then(([pending, approved, rejected]) => {
        if (!alive) return;
        setLists({ pending: pending || [], approved: approved || [], rejected: rejected || [] });
        setDenial(null);
      })
      .catch((error) => {
        if (!alive) return;
        const apiError = readApiError(error);
        // "Erro ao carregar" mentiria sobre um 403: não houve erro, houve bloqueio.
        if (apiError.kind) setDenial(apiError);
        else notifyModeration('Erro ao carregar a fila de moderação.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const subjects = useMemo(
    () =>
      (materias ?? [])
        .filter((m: any) => m?.id && m?.name)
        .map((m: any) => ({ id: String(m.id), name: String(m.name) })),
    [materias]
  );

  const visible = useMemo(() => {
    const term = fold(search.trim());
    const subject = subjects.find((s) => s.id === subjectFilter);
    return lists[status].filter((item) => {
      if (subjectFilter) {
        const matches =
          item.question?.subjectId === subjectFilter ||
          (!!subject && fold(item.question?.subject ?? '') === fold(subject.name));
        if (!matches) return false;
      }
      if (!term) return true;
      const haystack = fold(
        [
          item.author?.name,
          item.author?.label,
          item.body,
          item.question?.number,
          item.question?.subject,
          item.question?.agency,
          item.question?.year,
        ]
          .filter(Boolean)
          .join(' ')
      );
      return haystack.includes(term);
    });
  }, [lists, status, search, subjectFilter, subjects]);

  useEffect(() => {
    setFocusedIndex((index) => Math.min(index, Math.max(visible.length - 1, 0)));
  }, [visible.length]);

  const selectedVisible = useMemo(
    () => visible.filter((item) => selected.includes(item.id)).map((item) => item.id),
    [visible, selected]
  );

  const dropDraft = (id: string) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const closeReject = () => {
    setRejecting(null);
    setRejectReason(null);
    setRejectMessage('');
  };

  /** Sai de Pendentes e (quando houve decisão) entra na sub-fila de destino. */
  const settle = useCallback(
    (item: ModerationComment, target?: QueueStatus, patch?: Partial<ModerationComment>) => {
      setLists((prev) => ({
        ...prev,
        pending: prev.pending.filter((c) => c.id !== item.id),
        ...(target && target !== 'pending'
          ? { [target]: [{ ...item, ...patch, status: target }, ...prev[target]] }
          : {}),
      }));
      setSelected((prev) => prev.filter((id) => id !== item.id));
      onPendingResolved?.(1);
    },
    [onPendingResolved]
  );

  const approve = async (item: ModerationComment) => {
    if (busy.includes(item.id)) return;
    const draft = drafts[item.id];
    const edited = draft !== undefined && draft.trim() && draft.trim() !== item.body;
    setBusy((prev) => [...prev, item.id]);
    try {
      await ForumService.approveComment(item.id, edited ? draft.trim() : undefined);
      settle(item, 'approved', edited ? { body: draft.trim() } : undefined);
      dropDraft(item.id);
      if (rejecting === item.id) closeReject();
    } catch (error) {
      if (isAlreadyResolved(error)) {
        settle(item);
        notifyModeration('Este comentário já havia sido moderado por outro administrador.');
      } else if (!readApiError(error).kind) {
        notifyModeration('Não foi possível aprovar o comentário.');
      }
    } finally {
      setBusy((prev) => prev.filter((id) => id !== item.id));
    }
  };

  const confirmReject = async (item: ModerationComment) => {
    if (!rejectReason || busy.includes(item.id)) return;
    const payload = {
      reason: rejectReason,
      ...(rejectMessage.trim() ? { message: rejectMessage.trim() } : {}),
    };
    setBusy((prev) => [...prev, item.id]);
    try {
      await ForumService.rejectComment(item.id, payload);
      settle(item, 'rejected', {
        rejectReason: payload.reason,
        moderatorMessage: payload.message,
      });
      closeReject();
    } catch (error) {
      if (isAlreadyResolved(error)) {
        settle(item);
        closeReject();
        notifyModeration('Este comentário já havia sido moderado por outro administrador.');
      } else if (!readApiError(error).kind) {
        notifyModeration('Não foi possível rejeitar o comentário.');
      }
    } finally {
      setBusy((prev) => prev.filter((id) => id !== item.id));
    }
  };

  const approveSelected = async () => {
    const ids = selectedVisible;
    if (!ids.length || batching) return;
    setBatching(true);
    try {
      await ForumService.approveCommentsBatch(ids);
      const items = lists.pending.filter((item) => ids.includes(item.id));
      setLists((prev) => ({
        ...prev,
        pending: prev.pending.filter((item) => !ids.includes(item.id)),
        approved: [...items.map((item) => ({ ...item, status: 'approved' as const })), ...prev.approved],
      }));
      setSelected([]);
      onPendingResolved?.(items.length);
    } catch (error) {
      if (isAlreadyResolved(error)) {
        setLists((prev) => ({
          ...prev,
          pending: prev.pending.filter((item) => !ids.includes(item.id)),
        }));
        setSelected([]);
        onPendingResolved?.(ids.length);
        notifyModeration('Os comentários selecionados já haviam sido moderados.');
      } else if (!readApiError(error).kind) {
        notifyModeration('Não foi possível aprovar os comentários selecionados.');
      }
    } finally {
      setBatching(false);
    }
  };

  const focusCard = (index: number) => {
    setFocusedIndex(index);
    cardRefs.current[index]?.focus();
  };

  const openReject = (item: ModerationComment) => {
    setRejecting(item.id);
    setRejectReason(null);
    setRejectMessage('');
  };

  /**
   * `A`/`R`/`J`/`K` no card. Digitar numa caixa de texto nunca dispara ação —
   * é por isso que os atalhos não são um listener global.
   */
  const onCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    const target = event.target as HTMLElement;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable) return;

    const item = visible[index];
    const key = event.key.toLowerCase();

    if (key === 'j') {
      event.preventDefault();
      focusCard(Math.min(index + 1, visible.length - 1));
      return;
    }
    if (key === 'k') {
      event.preventDefault();
      focusCard(Math.max(index - 1, 0));
      return;
    }
    if (status !== 'pending' || !item) return;
    if (key === 'a') {
      event.preventDefault();
      approve(item);
      return;
    }
    if (key === 'r') {
      event.preventDefault();
      openReject(item);
    }
  };

  if (denial) return <AccessDenied kind={denial.kind!} detail={denial.raw} variant="panel" />;

  const pendingCount = lists.pending.length;

  return (
    <div data-testid="moderation-queue" className="flex flex-col gap-5">
      {/* Toolbar: sub-filas + busca + matéria + lote */}
      <div className="flex flex-wrap items-center gap-2.5">
        <div
          role="tablist"
          aria-label="Sub-filas de comentários"
          className="flex items-center gap-0.5 rounded-input bg-surface-muted p-0.5"
        >
          {STATUS_TABS.map((tab) => {
            const active = status === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setStatus(tab.key);
                  closeReject();
                  setFocusedIndex(0);
                }}
                className={[
                  'h-8 rounded-chip px-3 text-sm transition-colors duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60',
                  active
                    ? 'bg-select/[0.14] font-semibold text-select-ink'
                    : 'font-medium text-ink-secondary hover:bg-surface-hover hover:text-ink',
                ].join(' ')}
              >
                {tab.label}
                <span className="ml-1.5 text-xs text-ink-muted">{lists[tab.key].length}</span>
              </button>
            );
          })}
        </div>

        <label className="flex h-9 min-w-[260px] flex-1 items-center gap-2 rounded-input border border-edge bg-surface px-3">
          <Search size={15} className="shrink-0 text-ink-muted" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Buscar por aluno, questão ou trecho do comentário"
            placeholder="Buscar por aluno, questão ou trecho do comentário"
            className="h-full w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-muted"
          />
        </label>

        <div className="relative flex h-9 items-center gap-2 rounded-input border border-edge bg-surface px-3">
          <select
            value={subjectFilter}
            onChange={(event) => setSubjectFilter(event.target.value)}
            aria-label="Matéria"
            className="h-full appearance-none bg-transparent pr-5 text-sm text-ink-secondary outline-none"
          >
            <option value="">Matéria: Todas</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            className="pointer-events-none absolute right-3 text-ink-muted"
            aria-hidden
          />
        </div>

        {status === 'pending' && (
          <button
            type="button"
            onClick={approveSelected}
            disabled={!selectedVisible.length || batching}
            className={[
              'inline-flex h-9 items-center gap-2 rounded-input px-3.5 text-sm font-semibold',
              'bg-brand text-brand-contrast transition-[filter] hover:brightness-110',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60',
            ].join(' ')}
          >
            <CheckCheck size={15} aria-hidden />
            Aprovar selecionados ({selectedVisible.length})
          </button>
        )}
      </div>

      <div className="flex flex-col gap-5 lg:flex-row">
        {/* Fila */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          {loading && <p className="text-sm text-ink-muted">Carregando a fila…</p>}

          {!loading && !visible.length && (
            <div className="rounded-card border border-edge bg-surface p-8 text-center">
              <p className="text-sm font-medium text-ink">
                {status === 'pending'
                  ? 'Nenhum comentário pendente.'
                  : `Nenhum comentário ${status === 'approved' ? 'aprovado' : 'rejeitado'}.`}
              </p>
              {(search || subjectFilter) && (
                <p className="mt-1 text-xs text-ink-muted">
                  Nenhum resultado para o filtro aplicado.
                </p>
              )}
            </div>
          )}

          {visible.map((item, index) => {
            const draft = drafts[item.id];
            const editing = draft !== undefined;
            const isRejecting = rejecting === item.id;
            const phone = hasPhoneFlag(item);
            const reported = (item.flags ?? []).includes('reported');
            // O contexto do autor vem achatado no comentário (design.md:
            // `firstComment`, `approvedCount`, `rejectedCount`).
            const context = {
              firstComment: item.firstComment ?? false,
              approvedCount: item.approvedCount ?? 0,
              rejectedCount: item.rejectedCount ?? 0,
            };
            const focused = focusedIndex === index;

            return (
              <div
                key={item.id}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                data-testid={`mod-card-${item.id}`}
                role="article"
                aria-label={`Comentário de ${item.author?.name ?? 'aluno'}`}
                tabIndex={0}
                onFocus={() => setFocusedIndex(index)}
                onKeyDown={(event) => onCardKeyDown(event, index)}
                className={[
                  'rounded-card border bg-surface outline-none transition-colors duration-150',
                  focused ? 'border-select/[0.42] ring-2 ring-select/30' : 'border-edge',
                ].join(' ')}
              >
                <div className="flex gap-3.5 px-5 py-4">
                  {status === 'pending' && (
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 shrink-0 accent-[rgb(var(--c-primary))]"
                      aria-label={`Selecionar comentário de ${item.author?.name ?? 'aluno'}`}
                      checked={selected.includes(item.id)}
                      onChange={(event) =>
                        setSelected((prev) =>
                          event.target.checked
                            ? [...prev, item.id]
                            : prev.filter((id) => id !== item.id)
                        )
                      }
                    />
                  )}

                  <div
                    aria-hidden
                    className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-blue/[0.16] text-xs font-bold text-accent-blue-ink"
                  >
                    {initials(item.author?.name ?? '')}
                  </div>

                  <div className="flex min-w-0 flex-1 flex-col gap-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-ink">
                        {item.author?.name ?? 'Aluno'}
                      </span>
                      <span className="text-xs text-ink-muted">{roleLabel(item.author ?? {} as ForumAuthor)}</span>
                      <span className="flex-1" />
                      <time dateTime={item.createdAt} className="text-xs text-ink-muted">
                        {relativeTime(item.createdAt)}
                      </time>
                    </div>

                    <span className="inline-flex w-fit items-center gap-1.5 rounded-chip bg-surface-muted px-2 py-1 text-xs font-medium text-ink-secondary">
                      <FileQuestion size={13} aria-hidden />
                      {questionRefLabel(item)}
                    </span>

                    {editing ? (
                      <textarea
                        value={draft}
                        onChange={(event) =>
                          setDrafts((prev) => ({ ...prev, [item.id]: event.target.value }))
                        }
                        aria-label="Texto do comentário"
                        rows={4}
                        className="w-full rounded-input border border-edge bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-select/[0.42]"
                      />
                    ) : (
                      <p className="whitespace-pre-wrap text-sm text-ink">{item.body}</p>
                    )}

                    {/* Contexto do aluno + flags automáticas */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      {context.firstComment && (
                        <span className={`${CHIP} border-accent-blue/40 bg-accent-blue/[0.12] text-accent-blue-ink`}>
                          Primeiro comentário do aluno
                        </span>
                      )}
                      {context.approvedCount > 0 && (
                        <span className={`${CHIP} border-edge bg-surface-muted text-ink-secondary`}>
                          {context.approvedCount} aprovado{context.approvedCount > 1 ? 's' : ''}
                        </span>
                      )}
                      {context.rejectedCount > 0 && (
                        <span className={`${CHIP} border-edge bg-surface-muted text-ink-secondary`}>
                          {context.rejectedCount} rejeitado{context.rejectedCount > 1 ? 's' : ''} antes
                        </span>
                      )}
                      {phone && (
                        <span className={`${CHIP} border-warn/40 bg-warn/[0.12] text-warn-ink`}>
                          <Phone size={12} aria-hidden />
                          Contém telefone
                        </span>
                      )}
                      {reported && (
                        <span className={`${CHIP} border-warn/40 bg-warn/[0.12] text-warn-ink`}>
                          <Flag size={12} aria-hidden />
                          Denunciado por alunos
                        </span>
                      )}
                      {status === 'rejected' && item.rejectReason && (
                        <span className={`${CHIP} border-warn/40 bg-warn/[0.12] text-warn-ink`}>
                          <Ban size={12} aria-hidden />
                          {REJECT_REASONS.find((r) => r.key === item.rejectReason)?.label ??
                            item.rejectReason}
                        </span>
                      )}
                    </div>

                    {status === 'rejected' && item.moderatorMessage && (
                      <p className="text-xs text-ink-secondary">
                        Mensagem ao aluno: {item.moderatorMessage}
                      </p>
                    )}

                    {status === 'pending' && (
                      <div className="flex flex-wrap items-center gap-2 pt-0.5">
                        {editing ? (
                          <button
                            type="button"
                            className={BTN_NEUTRAL}
                            onClick={() => dropDraft(item.id)}
                          >
                            <X size={14} aria-hidden />
                            Cancelar edição
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={BTN_NEUTRAL}
                            onClick={() => setDrafts((prev) => ({ ...prev, [item.id]: item.body }))}
                          >
                            <Pencil size={14} aria-hidden />
                            Editar
                          </button>
                        )}
                        <button
                          type="button"
                          className={`${BTN} border-warn/40 bg-warn/[0.12] text-warn-ink hover:bg-warn/[0.18]`}
                          onClick={() => (isRejecting ? closeReject() : openReject(item))}
                        >
                          <Ban size={14} aria-hidden />
                          Rejeitar
                        </button>
                        <button
                          type="button"
                          className={`${BTN} border-transparent bg-brand text-brand-contrast hover:brightness-110`}
                          disabled={busy.includes(item.id)}
                          onClick={() => approve(item)}
                        >
                          <Check size={14} aria-hidden />
                          Aprovar
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {isRejecting && status === 'pending' && (
                  <div className="flex flex-col gap-3 rounded-b-card border-t border-warn/40 bg-warn/[0.08] px-5 py-4">
                    <p className="text-sm font-semibold text-ink">Motivo da rejeição</p>
                    <div
                      role="radiogroup"
                      aria-label="Motivo da rejeição"
                      className="flex flex-wrap gap-2"
                    >
                      {REJECT_REASONS.map((reason) => {
                        const active = rejectReason === reason.key;
                        return (
                          <label
                            key={reason.key}
                            className={[
                              'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs',
                              active
                                ? 'border-select/[0.42] bg-select/[0.14] font-semibold text-select-ink'
                                : 'border-edge bg-surface font-medium text-ink-secondary hover:bg-surface-hover',
                            ].join(' ')}
                          >
                            <input
                              type="radio"
                              className="sr-only"
                              name={`reject-reason-${item.id}`}
                              value={reason.key}
                              checked={active}
                              onChange={() => setRejectReason(reason.key)}
                            />
                            {active && <Check size={12} aria-hidden />}
                            {reason.label}
                          </label>
                        );
                      })}
                    </div>

                    <textarea
                      value={rejectMessage}
                      onChange={(event) => setRejectMessage(event.target.value)}
                      aria-label="Mensagem para o aluno (opcional)"
                      placeholder="Mensagem para o aluno (opcional) — ex.: não é permitido divulgar contatos ou material pirata."
                      rows={2}
                      className="w-full rounded-input border border-edge bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-select/[0.42]"
                    />

                    <div className="flex items-center gap-2">
                      <button type="button" className={BTN_NEUTRAL} onClick={closeReject}>
                        Cancelar
                      </button>
                      {/* danger só aqui: é a ação destrutiva da tela (§7/§54). */}
                      <button
                        type="button"
                        className={`${BTN} border-transparent bg-danger text-white hover:brightness-110 dark:text-canvas`}
                        disabled={!rejectReason || busy.includes(item.id)}
                        onClick={() => confirmReject(item)}
                      >
                        <Ban size={14} aria-hidden />
                        Confirmar rejeição
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Coluna lateral: KPIs do dia, diretrizes e atalhos */}
        <aside className="flex w-full shrink-0 flex-col gap-4 lg:w-[300px]">
          <section
            aria-label="Números de hoje"
            className="flex flex-col gap-3 rounded-card border border-edge bg-surface p-5"
          >
            <h3 className="text-xs font-semibold tracking-wide text-ink-muted">HOJE</h3>
            <div className="flex gap-3">
              {[
                { label: 'Aprovados', value: kpis ? String(kpis.today.approved) : '—' },
                { label: 'Rejeitados', value: kpis ? String(kpis.today.rejected) : '—' },
                {
                  label: 'Tempo médio',
                  value: kpis ? `${kpis.today.avgMinutes} min` : '—',
                },
              ].map((kpi) => (
                <div key={kpi.label} className="flex flex-1 flex-col">
                  <span className="text-lg font-semibold text-ink">{kpi.value}</span>
                  <span className="text-xs text-ink-muted">{kpi.label}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-ink-muted">
              {pendingCount} na fila de pendentes
            </p>
          </section>

          <section
            aria-label="Diretrizes do fórum"
            className="flex flex-col gap-3 rounded-card border border-edge bg-surface p-5"
          >
            <h3 className="text-xs font-semibold tracking-wide text-ink-muted">
              DIRETRIZES DO FÓRUM
            </h3>
            {GUIDELINES.map((rule) => (
              <div key={rule.text} className="flex items-start gap-2">
                {rule.allowed ? (
                  <Check size={14} className="mt-0.5 shrink-0 text-ok-ink" aria-hidden />
                ) : (
                  // O frame pinta este ✕ de vermelho; aqui ele é `warn`. Uma
                  // diretriz não é erro nem ação destrutiva, e `danger` fica
                  // reservado a "Confirmar rejeição" e "Desativar questão".
                  <X size={14} className="mt-0.5 shrink-0 text-warn-ink" aria-hidden />
                )}
                <span className="text-xs text-ink-secondary">{rule.text}</span>
              </div>
            ))}
          </section>

          <section
            aria-label="Atalhos de teclado"
            className="flex flex-col gap-3 rounded-card border border-edge bg-surface p-5"
          >
            <h3 className="text-xs font-semibold tracking-wide text-ink-muted">ATALHOS</h3>
            {[
              { label: 'Aprovar', keys: 'A' },
              { label: 'Rejeitar', keys: 'R' },
              { label: 'Próximo / anterior', keys: 'J / K' },
            ].map((shortcut) => (
              <div key={shortcut.label} className="flex items-center justify-between">
                <span className="text-xs text-ink-secondary">{shortcut.label}</span>
                <kbd className="rounded-chip border border-edge bg-surface-muted px-2 py-0.5 font-mono text-xs text-ink-secondary">
                  {shortcut.keys}
                </kbd>
              </div>
            ))}
          </section>
        </aside>
      </div>
    </div>
  );
}
